// "Call on this computer" in the Mac app: this Mac's own sound, captured by
// the app with a Core Audio process tap (no share picker) and handed to the
// page as 16 kHz PCM. This is the page's half of that protocol (mac-shell v1,
// beside desktop.ts, which stays as it is):
//
//   page → app  window.webkit.messageHandlers.corvusDesktop.postMessage(
//                 {type: "callAudio", action: "start" | "stop", session})
//   app → page  window.corvusDesktop.callAudio.state(session, "ok", rate)
//                 (first when the capture runs, then every 2 s while it lives),
//               .state(session, "denied" | "unavailable" | "ended"),
//               .push(session, pos, base64)   PCM16 little endian, `pos` = index
//                 at `rate` of the push's first frame since the session began
//   the app says it can: window.corvusDesktopFeatures.callAudio === 1 (a
//   document-start script the app adds; an older app has none)
//
// A session is a random id of 32 hex. Calls for another session, pushes before
// `ok`, and anything after the end are ignored. No push and no state for
// STALL_MS ends the link as stalled (the app's heartbeat keeps a quiet Mac
// alive). The pure parts are self-tested (desktopCallAudio.selftest.ts).

import { desktopHandler, isMacShell } from "./desktop";

export const CALL_AUDIO_FEATURE = 1;
/** Thrown by the start: the Mac does not let Corvus record its sound. */
export const CALL_MAC_DENIED = "CallMacDenied";
/** Thrown by the start: it could not happen (no app, the app refused, no answer). */
export const CALL_MAC_UNAVAILABLE = "CallMacUnavailable";
/** A system dialog may be open meanwhile. */
export const START_TIMEOUT_MS = 60_000;
export const STALL_MS = 10_000;
const CHECK_MS = 1_000;
const MIN_RATE = 8_000;
const MAX_RATE = 48_000;

export type CallAudioEndWhy = "ended" | "denied" | "stalled";
export interface CallAudioLink { readonly session: string; readonly rate: number; ended(): boolean; stop(): void; onEnded(fn: (why: CallAudioEndWhy) => void): void }
export interface CallAudioApi { state(session: string, what: string, rate?: number): void; push(session: string, pos: number, b64: string): void }
export interface CallAudioDeps { post(m: { type: "callAudio"; action: "start" | "stop"; session: string }): boolean; install(api: CallAudioApi): void; now(): number; setTimeout(fn: () => void, ms: number): unknown; clearTimeout(t: unknown): void; newSession(): string }

/** Pure: the app's document-start flag says it can capture call audio. */
export function featuresAllowCallAudio(scope: unknown): boolean {
  const f = (scope as { corvusDesktopFeatures?: { callAudio?: unknown } } | null | undefined)?.corvusDesktopFeatures;
  return f?.callAudio === CALL_AUDIO_FEATURE;
}

/** In the Mac app, and the app says it can hear this Mac's sound. */
export function nativeCallAudioAvailable(): boolean {
  return typeof window !== "undefined" && isMacShell() && featuresAllowCallAudio(window);
}

/** PCM16 little endian from base64 (an odd last byte is dropped). */
export function decodePcm16(b64: string): Int16Array {
  const bin = atob(b64);
  const view = new DataView(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) view.setUint8(i, bin.charCodeAt(i));
  const out = new Int16Array(bin.length >> 1);
  for (let i = 0; i < out.length; i++) out[i] = view.getInt16(i * 2, true);
  return out;
}

function named(name: string, message: string): Error {
  const e = new Error(message);
  e.name = name;
  return e;
}

type Handler = { state(what: string, rate?: number): void; push(pos: number, b64: string): void };
/** The live sessions of each deps, and whether its api is installed (once). */
const routes = new WeakMap<CallAudioDeps, Map<string, Handler>>();

function routesOf(deps: CallAudioDeps): Map<string, Handler> {
  let map = routes.get(deps);
  if (!map) {
    const m = new Map<string, Handler>();
    routes.set(deps, (map = m));
    deps.install({
      state: (session, what, rate) => m.get(String(session))?.state(String(what), rate),
      push: (session, pos, b64) => m.get(String(session))?.push(pos, b64),
    });
  }
  return map;
}

const browserDeps: CallAudioDeps = {
  post(m) {
    if (!isMacShell()) return false;
    const handler = desktopHandler(window);
    if (!handler) return false;
    try {
      handler.postMessage(m);
      return true;
    } catch {
      return false;
    }
  },
  install(api) {
    const desk = (window as Window & { corvusDesktop?: { callAudio?: CallAudioApi } }).corvusDesktop;
    if (desk) desk.callAudio = api; // the object exists from initDesktop in the Mac app
  },
  now: () => Date.now(),
  setTimeout: (fn, ms) => window.setTimeout(fn, ms),
  clearTimeout: (t) => window.clearTimeout(t as number),
  newSession() {
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
  },
};

/**
 * Ask the app for this Mac's sound: resolves on its first `ok` (with its rate), rejects
 * CALL_MAC_DENIED or CALL_MAC_UNAVAILABLE. Call straight from a tap (the app refuses a start
 * no click came before).
 */
export function startNativeCallAudio(onPush: (pos: number, pcm: Int16Array, rate: number) => void, deps: CallAudioDeps = browserDeps): Promise<CallAudioLink> {
  const map = routesOf(deps);
  const session = deps.newSession();
  return new Promise<CallAudioLink>((resolve, reject) => {
    let phase: "starting" | "live" | "over" = "starting";
    let rate = 0;
    let lastHeard = 0;
    let why: CallAudioEndWhy | null = null;
    let timer: unknown = null;
    const fns: Array<(why: CallAudioEndWhy) => void> = [];
    const post = (action: "start" | "stop") => deps.post({ type: "callAudio", action, session });
    const over = () => {
      phase = "over";
      map.delete(session);
      if (timer !== null) deps.clearTimeout(timer);
      timer = null;
    };
    const fail = (name: string, message: string, stop: boolean) => {
      over();
      if (stop) post("stop");
      reject(named(name, message));
    };
    const end = (w: CallAudioEndWhy) => {
      over();
      why = w;
      if (w === "stalled") post("stop");
      for (const fn of fns.splice(0)) fn(w);
    };
    const check = () => {
      timer = null;
      if (phase !== "live") return;
      if (deps.now() - lastHeard >= STALL_MS) return end("stalled");
      timer = deps.setTimeout(check, CHECK_MS);
    };
    const link: CallAudioLink = {
      session,
      get rate() { return rate; },
      ended: () => phase === "over",
      stop() {
        if (phase === "over") return;
        over();
        post("stop");
      },
      onEnded(fn) {
        if (why) fn(why);
        else if (phase !== "over") fns.push(fn);
      },
    };
    map.set(session, {
      state(what, r) {
        if (phase === "starting") {
          if (what === "ok") {
            if (typeof r !== "number" || !Number.isFinite(r) || r < MIN_RATE || r > MAX_RATE) return fail(CALL_MAC_UNAVAILABLE, `bad rate ${String(r)}`, true);
            rate = r;
            phase = "live";
            lastHeard = deps.now();
            if (timer !== null) deps.clearTimeout(timer);
            timer = deps.setTimeout(check, CHECK_MS);
            resolve(link);
          } else if (what === "denied") fail(CALL_MAC_DENIED, "the Mac does not let Corvus record its sound", false);
          else if (what === "unavailable" || what === "ended") fail(CALL_MAC_UNAVAILABLE, `the app answered ${what}`, false);
          return;
        }
        if (phase !== "live") return;
        if (what === "ok") {
          lastHeard = deps.now();
          if (typeof r === "number" && Number.isFinite(r) && r >= MIN_RATE && r <= MAX_RATE) rate = r;
        } else if (what === "denied") end("denied");
        else if (what === "ended" || what === "unavailable") end("ended");
      },
      push(pos, b64) {
        if (phase !== "live") return;
        lastHeard = deps.now();
        let pcm: Int16Array;
        try {
          pcm = decodePcm16(String(b64));
        } catch {
          return; // not base64: dropped, the link lives on
        }
        if (typeof pos === "number" && Number.isFinite(pos) && pcm.length) onPush(pos, pcm, rate);
      },
    });
    if (!post("start")) return fail(CALL_MAC_UNAVAILABLE, "the Mac app is not there", false);
    if (phase === "starting") timer = deps.setTimeout(() => { timer = null; if (phase === "starting") fail(CALL_MAC_UNAVAILABLE, "no answer from the app", true); }, START_TIMEOUT_MS);
  });
}
